/**
 * The Omni-Torn player database (owner, 2026-10-29: "We store everything. Older data is less trustworthy … EVERYTHING
 * that could be beneficial in any which way should be taken and sent to the server for storage. Don't waste api usage on
 * this"). The shared contract between the extension (what it observes and sends), the server (apps/api/src/player-db.ts:
 * keeps every observation with its time and source, newest first per kind) and the target picker (rotation through all
 * players that match, so stale ones get refreshed instead of the same 50 forever).
 *
 * Rules (docs/torn-rules-compliance.md): observations come from Torn's API answers the extension already gets, or from a
 * Torn page the player is actively viewing (sent only while visible and focused — core/page-data.ts). Nothing here makes
 * an extra Torn call by itself; what's stored is named in the terms table from PLAYER_DB_TERMS on.
 */

import { PLAYER_DB_FOREVER_TERMS } from './key-terms';

/** From this terms version on, observations about other players go to the player database (key-terms.ts). */
export const PLAYER_DB_TERMS = 6;

/**
 * From this terms version on, a player may record spies (spies.ts): kept for them and shared with their faction (faction
 * only), and the spy's total — to 2 significant figures — goes to the player database as an estimate with method 'spy'.
 * New sharing: needs its own terms version (key-terms.ts TERMS_VERSION must reach it, with the table saying so).
 */
export const SPY_TERMS = 8;

/**
 * From PLAYER_DB_FOREVER_TERMS (7, key-terms.ts) the player database takes EVERYTHING that could be useful (owner,
 * 2026-10-29: "We store EVERYTHING that could be useful in any which way"): whole fights from the API attack log (ids,
 * exact times, both players, respect, chain, every modifier and flag), the attack page's fight log (every turn, both
 * sides), the sender's own public profile (never their stats, money, energy, nerve, happy, cooldowns, items or anything only their own key
 * reads), and estimates worked out from fights. A sender on PLAYER_DB_TERMS only sends — and the server only keeps from
 * them — what version 6 named (observationForTerms).
 */

/** What an observation is about. Add kinds freely (the server stores any kind listed here). */
export const OBSERVATION_KINDS = [
  'profile', // name, level, rank, age (days), gender, role, signup, awards, donator, property, life, previous names, icons
  'faction', // faction id/name/tag, position, days in faction
  'company', // company id/name/type, position
  'status', // Okay/Hospital/Jail/Traveling/Abroad/Federal + description + until, life, revive setting / revive chance
  'activity', // last action (timestamp + Online/Idle/Offline)
  'travel', // destination, departed/arrival, method seen
  'estimate', // battle stats estimate, Omni-Torn's own: { total, method: 'rank' | 'fight' | 'pool' } (never another tool's; the fair fight goes with the fight, not here)
  'loadout', // weapons/armor seen on the attack page
  'fight', // a fight with this player as opponent: how it went for them, their side, the fair fight; from terms 7 everything the API attack log says (FightRecord)
  'fightlog', // terms 7: a whole fight as the attack page showed it, turn by turn (FightLog), about the opponent
  'bounty', // bounties on them
  'personalstats', // public personal stats (API: xanax, attacks won/lost, networth public, etc.)
  'networth', // public networth / money in bazaar etc. where Torn shows it
  'social', // spouse, married days, friends/enemies counts, forum posts, karma — as shown on the profile
  'market', // bazaar open/closed, how many listings
] as const;
export type ObservationKind = (typeof OBSERVATION_KINDS)[number];

/**
 * Where an observation came from — only what Torn itself showed: 'page' = a Torn page the sender was viewing; 'api' = a
 * Torn API answer the sender's own key got for a feature; 'fight' = the sender's own API attack log; 'server' = the
 * Omni-Torn server's own reads (never accepted from a sender). Other tools' answers (FF Scouter, TornStats, KAL, S&R)
 * are never taken: they were fetched under those tools' terms for the player's own use.
 */
export type ObservationSource = 'page' | 'api' | 'fight' | 'server';
/** The sources POST /api/players/db/observe accepts from a sender (the server skips any other). */
export const OBSERVATION_SOURCES: readonly ObservationSource[] = ['page', 'api', 'fight'];

/** How much a source is believed when fresh (multiplied with trustOf's age factor in PlayerSummary.latest[kind].trust). */
export const SOURCE_TRUST: Record<ObservationSource, number> = { page: 1, api: 1, fight: 1, server: 1 };

/** trustOf × SOURCE_TRUST — what GET /api/players/db answers as `trust`. */
export const observationTrust = (kind: ObservationKind, source: ObservationSource, ageMs: number): number => trustOf(kind, ageMs) * (SOURCE_TRUST[source] ?? 0.8);

export interface Observation {
  /** The player it's about. */
  id: number;
  kind: ObservationKind;
  /** When it was true (ms, the observer's clock; the server clamps to its own now). */
  at: number;
  source: ObservationSource;
  /** The facts, kind-specific, small plain JSON (≤ 4 KB). Unknown fields are kept as they are. */
  data: Record<string, unknown>;
}

/**
 * Kinds that change quickly: the server keeps only the newest OBSERVE_KEEP_FAST of each per player (older ones say little);
 * every other kind keeps up to OBSERVE_KEEP_SLOW per player — a safety net: from terms version 7 (PLAYER_DB_FOREVER_TERMS)
 * the database keeps everything else with no end date. Rows sent under version 6 go after OBSERVE_KEEP_DAYS (what 6
 * promised: 2 years) unless their sender has since agreed to 7.
 */
export const FAST_KINDS: readonly ObservationKind[] = ['status', 'activity', 'travel', 'market', 'bounty'];
export const OBSERVE_KEEP_FAST = 100;
export const OBSERVE_KEEP_SLOW = 2_000;
export const OBSERVE_KEEP_DAYS = 730;
/** An observation's `at` older than this is refused (newer than the server's clock: taken as now). */
export const OBSERVE_MAX_AGE_DAYS = 400;

/** POST /api/players/db/observe — at most this many per call; the server keeps at most OBSERVE_MAX_PER_MINUTE per sender. */
export const OBSERVE_MAX_BATCH = 500;
export const OBSERVE_MAX_PER_MINUTE = 2_000;
export const OBSERVATION_MAX_BYTES = 4_096;
/** Kinds allowed to be bigger (a whole fight log, turn by turn). */
export const OBSERVATION_MAX_BYTES_BY_KIND: Partial<Record<ObservationKind, number>> = { fightlog: 32_768 };
/** The most one observation of this kind may be (its data as plain JSON). */
export const observationMaxBytes = (kind: string): number => OBSERVATION_MAX_BYTES_BY_KIND[kind as ObservationKind] ?? OBSERVATION_MAX_BYTES;
/** POST /api/players/db/observe: the most one call's body may be (the sender splits batches to fit). */
export const OBSERVE_MAX_BODY_BYTES = 3_000_000;

export interface ObserveBody {
  observations: Observation[];
}

/**
 * POST /api/players/db/observe answers. `kept` = stored or merged into an identical newer-in-time one (the same fact seen
 * again: one row, its time moved on); `skipped` = refused (bad kind/id/source/time/size, or over the per-minute limit).
 * Not agreed to terms PLAYER_DB_TERMS: `{ ok: false, message }` and nothing is kept.
 */
export interface ObserveAnswer {
  ok: boolean;
  kept: number;
  skipped: number;
  message?: string;
}

/**
 * What the server's `players` summary row (the pick query's filters) reads from each kind's data. Collectors: put these
 * fields in (others are kept as they are, for later). Times may be ms or Torn's seconds (below 1e12 = seconds).
 * - profile: { name, level, factionId?, lastAction?, status? (as `status`) }
 * - faction: { factionId (0 = in no faction), name?, tag?, position? }
 * - status: { state: 'Okay' | 'Hospital' | 'Jail' | 'Traveling' | 'Abroad' | 'Federal' | 'Fallen', until?, description? }
 * - activity: { lastAction (when, ms or s), state?: 'Online' | 'Idle' | 'Offline' }
 * - estimate: { total (battle stats total), method: 'fight' | 'pool' | 'rank' | 'spy' } — the summary's total is bestEstimate over all
 *   of a player's estimates (weighted by method and age), totalSource its heaviest method
 * - any kind: `name` and `level` when present.
 */
export interface SummaryFacts {
  name?: string;
  level?: number;
  factionId?: number;
  total?: number;
  totalSource?: string;
  lastAction?: number;
  status?: string;
  statusUntil?: number | null;
}

/**
 * Our own stat estimates (v0.116.0, "Our own stat estimates" phase 1). How an 'estimate' observation was worked out
 * (`data.method`), and how much each is believed when fresh:
 * - 'fight': from a fight the sender won, on the sender's own computer (the fair fight with their own battle stat score —
 *   core/estimates.ts); only the opponent's total goes, to 2 significant figures, never the sender's stats. The fight
 *   itself does carry the fair fight (and from terms 7 everything Torn shows of it, exact times included), so the server
 *   could work out the sender's stats from the two — the terms table says so, and they are never shown or shared with
 *   other players;
 * - 'pool': the server's own, from the anonymous fair-fight reading pool (apps/api/src/pool-estimates.ts);
 * - 'rank': from Torn's rank words — a wide guess;
 * - 'spy': a Torn spy a player recorded (spies.ts; from terms SPY_TERMS) — Torn's own numbers, the most trusted: a fresh one
 *   outweighs every other method outright (a fresh fight is under ESTIMATE_KEEP_SHARE of it) until it's about 10 days old,
 *   then is averaged with fresher estimates ("a recent real spy beats any estimate; an old spy beats a fresh estimate only
 *   up to a point").
 */
export const ESTIMATE_METHODS = ['fight', 'pool', 'rank', 'spy'] as const;
export type EstimateMethod = (typeof ESTIMATE_METHODS)[number];
export const ESTIMATE_METHOD_TRUST: Record<EstimateMethod, number> = { fight: 1, pool: 0.8, rank: 0.05, spy: 5 };
/** Estimates whose weight is below this share of the heaviest one are left out (a fresh fight outweighs a rank guess outright). */
export const ESTIMATE_KEEP_SHARE = 0.25;

/** A number to `digits` significant figures (estimates are sent and kept no more exactly than 2). */
export function roundSignificant(x: number, digits = 2): number {
  if (!Number.isFinite(x) || x === 0) return x;
  const p = Math.pow(10, Math.floor(Math.log10(Math.abs(x))) - digits + 1);
  return Math.round(x / p) * p;
}

/** How an estimate observation was worked out: its `method`, else its `source` word, else from where it came. */
export function estimateMethod(data: Record<string, unknown>, source: ObservationSource): EstimateMethod {
  const m = typeof data.method === 'string' ? data.method : typeof data.source === 'string' ? data.source : '';
  if ((ESTIMATE_METHODS as readonly string[]).includes(m)) return m as EstimateMethod;
  return source === 'fight' ? 'fight' : source === 'server' ? 'pool' : 'rank';
}

/**
 * The database's best estimate for one player from their estimate observations: each weighs its method's trust × its
 * age's (trustOf 'estimate': halving every 30 days); those under ESTIMATE_KEEP_SHARE of the heaviest are left out, the
 * rest averaged on a log scale by weight (several fights from different senders agree into one). `source` = the method
 * of the heaviest, `at` = its time. Null when none has a total.
 */
export function bestEstimate(list: ReadonlyArray<{ at: number; source: ObservationSource; data: Record<string, unknown> }>, now: number): { total: number; source: EstimateMethod; at: number; weight: number } | null {
  const rows: Array<{ ln: number; w: number; method: EstimateMethod; at: number }> = [];
  for (const o of list) {
    const total = o.data.total;
    if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0 || total >= 1e16) continue;
    const method = estimateMethod(o.data, o.source);
    const w = ESTIMATE_METHOD_TRUST[method] * trustOf('estimate', now - o.at);
    if (w > 0) rows.push({ ln: Math.log(total), w, method, at: o.at });
  }
  if (!rows.length) return null;
  const top = rows.reduce((a, b) => (b.w > a.w || (b.w === a.w && b.at > a.at) ? b : a));
  const kept = rows.filter((r) => r.w >= top.w * ESTIMATE_KEEP_SHARE);
  const sw = kept.reduce((a, r) => a + r.w, 0);
  const ln = kept.reduce((a, r) => a + r.w * r.ln, 0) / sw;
  return { total: Math.round(Math.exp(ln)), source: top.method, at: top.at, weight: Math.round(top.w * 1000) / 1000 };
}

const STATUS_STATES = ['okay', 'hospital', 'jail', 'traveling', 'abroad', 'federal', 'fallen'] as const;
const asMs = (x: unknown): number | undefined => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? (x < 1e12 ? Math.round(x * 1000) : Math.round(x)) : undefined);
const asInt = (x: unknown, max: number): number | undefined => (typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= max ? x : undefined);

function statusFacts(x: unknown): Pick<SummaryFacts, 'status' | 'statusUntil'> {
  if (typeof x === 'string') x = { state: x };
  if (!x || typeof x !== 'object') return {};
  const o = x as Record<string, unknown>;
  const state = typeof o.state === 'string' ? o.state.toLowerCase() : '';
  const s = (STATUS_STATES as readonly string[]).find((v) => v === state || (v === 'traveling' && state === 'travelling'));
  return s ? { status: s, statusUntil: asMs(o.until) ?? null } : {};
}

/** The summary fields one observation gives (the server keeps the newest of each). */
export function summaryFacts(kind: ObservationKind, data: Record<string, unknown>): SummaryFacts {
  const f: SummaryFacts = {};
  // A faction's or company's sighting names the faction or company, not the player (S25: it took over player names).
  if (kind !== 'faction' && kind !== 'company' && typeof data.name === 'string' && /^[\w-]{1,30}$/.test(data.name.trim())) f.name = data.name.trim();
  const level = asInt(data.level, 100);
  if (level !== undefined && level >= 1) f.level = level;
  const faction = asInt(data.factionId, 1e8);
  if ((kind === 'profile' || kind === 'faction') && faction !== undefined) f.factionId = faction;
  if (kind === 'estimate' && typeof data.total === 'number' && Number.isFinite(data.total) && data.total > 0 && data.total < 1e16) {
    f.total = Math.round(data.total);
    if (typeof data.source === 'string' && data.source.length <= 30) f.totalSource = data.source;
  }
  if (kind === 'activity' || kind === 'profile') {
    const la = asMs(data.lastAction);
    if (la !== undefined) f.lastAction = la;
  }
  if (kind === 'status') Object.assign(f, statusFacts(data));
  if (kind === 'profile' && data.status !== undefined) Object.assign(f, statusFacts(data.status));
  return f;
}

/** The latest of each kind for one player, with its age, as GET /api/players/db?ids=1,2,3 answers (≤ 100 ids). */
export interface PlayerSummary {
  id: number;
  name: string | null;
  /** kind → the newest observation of it (its data, when, source, how much to trust it 0–1 by age and source). */
  latest: Partial<Record<ObservationKind, { at: number; source: ObservationSource; trust: number; data: Record<string, unknown> }>>;
  /** How many observations are kept in all, and the oldest one's time. */
  count: number;
  firstSeen: number;
}

/** GET /api/players/db?ids= answers `{ players }` (unknown ids left out); POST /api/targets/pick answers `{ targets }`. */
export interface PlayerDbAnswer {
  players: PlayerSummary[];
}
export interface TargetPickAnswer {
  targets: TargetCandidateRow[];
}

/**
 * How much an observation is trusted, by its age (and kind): 1 when fresh, halving every `halfLifeMs`. Status changes in
 * minutes, activity in hours, level/faction in weeks, battle stats in weeks to months.
 */
export const TRUST_HALF_LIFE_MS: Record<ObservationKind, number> = {
  status: 10 * 60_000,
  activity: 6 * 3_600_000,
  travel: 2 * 3_600_000,
  bounty: 6 * 3_600_000,
  market: 24 * 3_600_000,
  profile: 14 * 86_400_000,
  faction: 14 * 86_400_000,
  company: 14 * 86_400_000,
  social: 30 * 86_400_000,
  networth: 7 * 86_400_000,
  personalstats: 14 * 86_400_000,
  estimate: 30 * 86_400_000,
  loadout: 30 * 86_400_000,
  fight: 60 * 86_400_000,
  fightlog: 60 * 86_400_000,
};

export function trustOf(kind: ObservationKind, ageMs: number): number {
  const h = TRUST_HALF_LIFE_MS[kind] ?? 7 * 86_400_000;
  return Math.max(0, Math.min(1, Math.pow(0.5, Math.max(0, ageMs) / h)));
}

/**
 * POST /api/targets/pick — candidates for a target list or the random-attack button. The server answers players that
 * match, rotating: those served least recently (to anyone) and checked least recently come first, mixed with a few known
 * good ones, so every matching player gets refreshed in turn. The client ranks them with its own stats (never sent).
 */
export interface TargetQuery {
  levelMin?: number;
  levelMax?: number;
  /** Estimated battle stat total range (the client turns its fair-fight wish into this with its own stats). */
  totalMin?: number;
  totalMax?: number;
  /** Not in a faction / only factionless, any. */
  faction?: 'any' | 'none' | 'some';
  /** Last action no more than this many days ago (inactive targets are often wanted) or at least. */
  inactiveDaysMin?: number;
  inactiveDaysMax?: number;
  /** Exclude these (the asker's faction mates, recent picks; ≤ TARGET_MAX_EXCLUDE). */
  exclude?: number[];
  /** With a total range: also players with no estimate yet (they're worth checking). Default false. */
  includeUnknownTotal?: boolean;
  /** How many (≤ 50). */
  count?: number;
  /**
   * Event presets (website roadmap S16, FF Scouter's "not in hospital"): only players seen out of hospital — Okay, or
   * their hospital or jail time run out — within this many hours (1–TARGET_OKAY_MAX_HOURS). Players whose status nobody
   * has seen lately are left out.
   */
  okayWithinHours?: number;
  /**
   * Hospital-exit window (S16): also players whose hospital time ends within this many minutes
   * (1–TARGET_LEAVING_MAX_MINUTES); the answer then comes soonest out first (those already out, then by the time they leave).
   */
  leavingWithinMinutes?: number;
  /** Only these players (S16: an Elimination team's members, read with the viewer's own key; ≤ TARGET_MAX_IDS). */
  ids?: number[];
}

export const TARGET_MAX_COUNT = 50;
export const TARGET_MAX_EXCLUDE = 1_000;
/** POST /api/targets/pick `ids`: at most this many (an Elimination team's members). */
export const TARGET_MAX_IDS = 1_000;
export const TARGET_OKAY_MAX_HOURS = 48;
export const TARGET_LEAVING_MAX_MINUTES = 180;
/** GET /api/players/db?ids= — at most this many ids. */
export const PLAYER_DB_MAX_IDS = 100;

/** The owner's numbers (GET /api/owner/player-db/stats). */
export interface PlayerDbStats {
  players: number;
  observations: number;
  /** Observations received in the last hour. */
  lastHour: number;
  /** The biggest kinds, most observations first. */
  kinds: Array<{ kind: string; count: number }>;
  /** Players picked as targets in the last hour. */
  servedLastHour: number;
}

export interface TargetCandidateRow {
  id: number;
  name: string | null;
  level: number | null;
  factionId: number | null;
  /** The database's best estimate (bestEstimate: our own estimates weighted by method and age). */
  total: number | null;
  /** How the heaviest estimate behind `total` was worked out: 'fight' | 'pool' | 'rank' | 'spy'. */
  totalSource: string | null;
  totalAt: number | null;
  lastAction: number | null;
  status: string | null;
  statusUntil: number | null;
  /** When anyone last checked this player with Torn (ms) — older = more worth checking. */
  checkedAt: number | null;
  /** Picked as a known-good one (fresh estimate, recently okay) rather than by rotation. */
  knownGood?: boolean;
}

// ---------------------------------------------------------------- what each terms version lets a sender send

/**
 * The sender's own public facts (terms PLAYER_DB_FOREVER_TERMS): only what any profile shows about them (life too) — never their battle
 * stats, money, energy, nerve, happy, cooldowns, items, points, estimates of them, their fights, loadout, personal stats (which the
 * sender's own key reads with private ones among them) or anything only their own key reads. Each kind lists the fields that may go; anything else in it is dropped; kinds not listed never go.
 */
export const SELF_PUBLIC_FIELDS: Partial<Record<ObservationKind, readonly string[]>> = {
  profile: ['name', 'level', 'rank', 'title', 'age', 'gender', 'role', 'signup', 'awards', 'honor', 'donator', 'property', 'propertyId', 'life', 'maxLife', 'revivable', 'competition', 'previousNames', 'medals', 'badges'],
  faction: ['factionId', 'name', 'tag', 'position', 'daysInFaction'],
  company: ['companyId', 'name', 'type', 'position'],
  status: ['state', 'description', 'details', 'until', 'color', 'revivable'],
  activity: ['lastAction', 'state'],
  travel: ['destination', 'leg', 'until'],
  social: ['married', 'spouseId', 'spouseName', 'marriedDays', 'friends', 'enemies', 'forumPosts', 'karma'],
  market: ['bazaarOpen'],
  bounty: ['reward', 'reason'],
};

/** What a fight observation said under terms version 6: how it went for the opponent, their side, the fair fight (API log); what they had and used (attack page). */
export const V6_FIGHT_FIELDS: readonly string[] = ['side', 'result', 'fairFight', 'seenOn', 'weapons', 'items'];

const pick = (data: Record<string, unknown>, fields: readonly string[]): Record<string, unknown> => Object.fromEntries(fields.filter((k) => data[k] !== undefined).map((k) => [k, data[k]]));
const hourStart = (ms: number): number => Math.floor(ms / 3_600_000) * 3_600_000;

/**
 * One observation as a sender on terms version `terms` may send it (the extension before sending; the server again before
 * keeping — the same rules both sides), or null when it may not go at all:
 * - below PLAYER_DB_TERMS: nothing; a spy's estimate (method 'spy') only from SPY_TERMS;
 * - about the sender themself: from PLAYER_DB_FOREVER_TERMS only, and only SELF_PUBLIC_FIELDS (never a fight, an estimate,
 *   a loadout or stats of theirs — so nothing a player sends about themself can feed an estimate of them);
 * - on PLAYER_DB_TERMS (6): no 'fightlog', no fight estimate (named from 7), a 'fight' only with V6_FIGHT_FIELDS, and
 *   anything learnt from the API attack log (source 'fight') at its hour only;
 * - from PLAYER_DB_FOREVER_TERMS: everything else as it is.
 */
export function observationForTerms<T extends { id: number; kind: string; at: number; source: string; data: Record<string, unknown> }>(o: T, terms: number, senderId: number | null): T | null {
  if (!(terms >= PLAYER_DB_TERMS)) return null;
  // A spy's estimate only from senders who agreed to the spy terms (new sharing: SPY_TERMS).
  if (o.kind === 'estimate' && terms < SPY_TERMS && estimateMethod(o.data, o.source as ObservationSource) === 'spy') return null;
  const all = terms >= PLAYER_DB_FOREVER_TERMS;
  if (senderId !== null && o.id === senderId) {
    const fields = all ? SELF_PUBLIC_FIELDS[o.kind as ObservationKind] : undefined;
    if (!fields) return null;
    const data = pick(o.data, fields);
    return Object.keys(data).length ? { ...o, data } : null;
  }
  if (all) return o;
  if (o.kind === 'fightlog') return null;
  if (o.kind === 'estimate' && estimateMethod(o.data, o.source as ObservationSource) === 'fight') return null;
  if (o.kind === 'fight') {
    const data = pick(o.data, V6_FIGHT_FIELDS);
    return Object.keys(data).length ? { ...o, at: hourStart(o.at), data } : null;
  }
  // Version 6 said a fight's time goes to the hour only: so does anything else learnt from one.
  return o.source === 'fight' ? { ...o, at: hourStart(o.at) } : o;
}

/**
 * The 'fight' observation's data from a sender on terms 7 (API attack log; the opponent is the observation's player):
 * - v6's: side (theirs: 'attacker' | 'defender'), result (how it went for THEM: 'won' | 'lost' | 'draw'), fairFight
 *   (only where Torn's number means something: the sender's attacks they won);
 * - attackId, code (Torn's attack log id and code), startedAt, endedAt (Torn's seconds, exact), tornResult (Torn's word);
 * - attacker, defender: { id, name, level, factionId, factionName } (attacker null when stealthed);
 * - outcome: { attacker, defender } — how it went for each side;
 * - respectGain, respectLoss, chain, modifiers (every one Torn gives: fair_fight, war, retaliation, group, overseas,
 *   chain, warlord …), stealthed, raid, rankedWar, interrupted, finishingHitEffects, extra (any other field of Torn's,
 *   as it is).
 */
export type FightRecord = Record<string, unknown>;

/**
 * The 'fightlog' observation's data (terms 7; the attack page while viewed, recorded when the fight ends; about the
 * opponent): fightId, logId, startedAt/endedAt (Torn's seconds), attacker/defender (the opponent's side as Torn gave it;
 * the sender's own side only their public facts), winner, result/outcome/info and mug (from Torn's end result), stats
 * (hits, damage, moves per player), life (both players' life at the end), turns (oldest first: who, Torn's result word,
 * damage, weapon, the body part, life before/after where Torn says, the line as shown), effects (temporary effects seen),
 * opponentLoadout, ammo (clips as Torn showed them — never the sender's ammo stock). See core/player-observe.ts fromFightLog.
 */
export type FightLog = Record<string, unknown>;
